import { defineRequest, defineRequestWithParam } from "../axios"
import type {
    CampaignABTestResultResponse,
    CampaignBehaviorSummaryResponse,
    CampaignCopyResponse,
    CampaignCreateResponse,
    CampaignDetailResponse,
    CampaignLinkAnalyticsResponse,
    CampaignListResponse,
    CampaignMemberListResponse,
    CampaignOptionListResponse,
    CampaignReview,
    CampaignReviewersResponse,
    CampaignStatusCountResponse,
    CampaignStatusListResponse,
    CampaignSummaryResponse,
    CampaignUpdateResponse,
    CreateCampaignPayload,
    ExportCampaignMemberListPayload,
    PatchCampaignPayload,
    ReviewCampaignPayload,
} from "./types"

// 取得 campaign 列表
export const getCampaignList = defineRequest<CampaignListResponse>({
    url: "/campaigns/",
    method: "get",
})

// 新增 campaign
export const createCampaign = defineRequest<CampaignCreateResponse, CreateCampaignPayload>({
    url: "/campaigns/",
    method: "post",
})

// 複製 campaign
export const copyCampaign = defineRequestWithParam<CampaignCopyResponse>({
    url: (id: string) => `/campaigns/${id}/copy/`,
    method: "post",
})

// 取得單一 campaign
export const getCampaign = defineRequestWithParam<CampaignDetailResponse>({
    url: (id: string) => `/campaigns/${id}/`,
    method: "get",
})

// 更新 campaign
export const updateCampaign = defineRequestWithParam<CampaignUpdateResponse>({
    url: (id: string) => `/campaigns/${id}/`,
    method: "put",
})

// 部分更新 campaign
export const patchCampaign = defineRequestWithParam<CampaignUpdateResponse, PatchCampaignPayload>({
    url: (id: string) => `/campaigns/${id}/`,
    method: "patch",
})

// 刪除 campaign
export const deleteCampaign = defineRequestWithParam<void>({
    url: (id: string) => `/campaigns/${id}/`,
    method: "delete",
})

// 取得單一 campaign 的分析資料
export const getCampaignAnalytics = defineRequestWithParam<CampaignSummaryResponse>({
    url: (id: string) => `/campaigns/${id}/analytics/`,
    method: "get",
})

// 取得單一 campaign A/B test的分析資料
export const getCampaignABTestResult = defineRequestWithParam<CampaignABTestResultResponse>({
    url: (id: string) => `/campaigns/${id}/analytics/ab-test-result/`,
    method: "get",
})

// 取得單一 campaign 數據詳情資訊
export const getCampaignBehaviorAnalytics = defineRequestWithParam<CampaignBehaviorSummaryResponse>({
    url: (id: string) => `/campaigns/${id}/analytics/behavior-summary/`,
    method: "get",
})

// 取得單一 campaign 數據的點擊成效資訊
export const getCampaignLinkAnalytics = defineRequestWithParam<CampaignLinkAnalyticsResponse>({
    url: (id: string) => `/campaigns/${id}/analytics/campaign-link/`,
    method: "get",
})

// 取得單一 campaign 活動名單
export const getCampaignMemberList = defineRequestWithParam<CampaignMemberListResponse>({
    url: (id: string) => `/campaigns/${id}/analytics/member-list/`,
    method: "get",
})

// 匯出單一 campaign 活動名單
export const exportCampaignMemberList = defineRequestWithParam<void, ExportCampaignMemberListPayload>({
    url: (id: string) => `/campaigns/${id}/analytics/member-list/export/`,
    method: "post",
})

// 提交 campaign 送審
export const submitCampaign = defineRequestWithParam<void>({
    url: (id: string) => `/campaigns/${id}/submit/`,
    method: "post",
})

// 獲得 campaign 的審核者
export const getCampaignReviewers = defineRequestWithParam<CampaignReviewersResponse>({
    url: (id: string) => `/campaigns/${id}/reviewer/`,
    method: "get",
})

// 審核 campaign
export const reviewCampaign = defineRequestWithParam<CampaignReview, ReviewCampaignPayload>({
    url: (id: string) => `/campaigns/${id}/review/`,
    method: "post",
})

// 取消活動
export const cancelCampaign = defineRequestWithParam<void>({
    url: (uuid: string) => `/campaigns/${uuid}/cancel/`,
    method: "post",
})

// 取得 campaign 各狀態數量
export const getCampaignStatusCount = defineRequest<CampaignStatusCountResponse>({
    url: "/campaigns/counts/",
    method: "get",
})

// 取得 campaign 活動管道
export const getCampaignMediaType = defineRequest<CampaignOptionListResponse>({
    url: "/campaigns/media-type/",
    method: "get",
})

// 取得 campaign 類型
export const getCampaignType = defineRequest<CampaignOptionListResponse>({
    url: "/campaigns/type/",
    method: "get",
})

// 取得 campaign 活動狀態
export const getCampaignStatus = defineRequest<CampaignStatusListResponse>({
    url: "/campaigns/status/",
    method: "get",
})
